import React, { useEffect } from 'react';
import { useStore } from '../../../../../../stores/root';

// assets
import { faCheck, faComment, faCopy, faDownload, faEdit, faHistory, faPaperPlane, faPlus, faReceipt, faRotate, faXmarkCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// services

// props

// components
import { ActionButton, CommandBar, DefaultButton, Dropdown, ICommandBarItemProps, MessageBar, MessageBarType, Panel, PanelType, Pivot, PivotItem, PrimaryButton, Spinner, SpinnerSize, Stack, TextField } from '@fluentui/react';
import { IIncomeInvoiceResourceProps, IIncomeInvoiceResourceShortProps } from '../../../../../../props/finance/incomes/invoices/invoice';
import IncomeInvoiceService from '../../../../../../services/finance/incomes/invoices/invoice';
import Label from '../../../../../typography/label';
import Tag from '../../../../../uiframeworks/tag';
import Text from '../../../../../typography/text';
import moment from 'moment';
import OrderService from '../../../../../../services/orders/order';
import GeneralService from '../../../../../../services/general';
import ErrorService from '../../../../../../services/general/error';
import { observer } from 'mobx-react';
import PermissionsService from '../../../../../../services/permissions';
import LoadingDialogComponent from '../../../../../feedbacks/loadingDialog';
import IncomeInvoiceDocumentsList from '../../documents/general/list';
import GlobalConfig from '../../../../../../config';
import IncomeInvoicePaymentList from '../../payments/general/list';
import IncomeInvoicePaymentForm from '../../payments/general/form';
import IncomeInvoiceRelatedItems from './relatedItems';
import Details from '../../../../../typography/details';
import IncomeInvoiceCancelForm from '../form/cancel';
import SimpleMessageBarComponent from '../../../../../feedbacks/simpleMessageBar';
import IncomeInvoiceForm from '../form';
import IncomePaymentForm from '../../../payments/general/form';
import IncomePaymentsList from '../../../payments/general/list';
import OrdersList from '../../../../../orders/general/list';
import IncomeInvoiceOrderItems from './orderitems';
import IncomeInvoiceVersionHistory from '../versions';
import IncomeInvoicePartForm from '../../parts/general/form';
import IncomeInvoicePartsList from '../../parts/general/list';
import { templateTagihan } from '../../../../../../manifests/templateInvoices';

type IncomeInvoiceDetailsProps = {
    invoiceId: string;
    onDismissed(refresh?: boolean): void;
}

const IncomeInvoiceDetails: React.FC<IncomeInvoiceDetailsProps> = observer((props: IncomeInvoiceDetailsProps) => {
    const { banner, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [invoice, setInvoice] = React.useState<IIncomeInvoiceResourceProps | undefined>();
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();
    const [downloading, setDownloading] = React.useState<boolean>(false);

    const [reminderText, setReminderText] = React.useState<string>('');
    const [detectedCategory, setDetectedCategory] = React.useState<string>('');
    const [randomTrigger, setRandomTrigger] = React.useState<number>(0);

    useEffect(() => {
        if (invoice && activeSurface === 'invoice.reminder') {
            const today = moment().startOf('day');
            const due = moment(invoice.dueDate).startOf('day');

            let category: 'pre_due_reminder' | 'due_today' | 'overdue' | 'follow_up' = 'pre_due_reminder';
            let categoryLabel = 'Pra Jatuh Tempo';

            if (today.isBefore(due)) {
                category = 'pre_due_reminder';
                categoryLabel = 'Pra Jatuh Tempo (Pre-due)';
            } else if (today.isSame(due)) {
                category = 'due_today';
                categoryLabel = 'Jatuh Tempo Hari Ini (Due Today)';
            } else {
                const diffDays = today.diff(due, 'days');
                if (diffDays > 7) {
                    category = 'follow_up';
                    categoryLabel = 'Follow Up Lanjutan';
                } else {
                    category = 'overdue';
                    categoryLabel = 'Lewat Jatuh Tempo (Overdue)';
                }
            }

            setDetectedCategory(categoryLabel);

            const templates = templateTagihan[category];
            const randIdx = Math.floor(Math.random() * templates.length);
            const template = templates[randIdx];

            const custName = invoice.order?.customer?.name;
            const compName = invoice.order?.company?.name || invoice.company?.name;
            const clientName = custName && compName ? `${custName} - ${compName}` : (custName || compName || 'Bapak/Ibu');
            const invoiceNo = IncomeInvoiceService.getInvoiceNumber(invoice.id);
            const dateStr = invoice.dueDate ? moment(invoice.dueDate).format("DD/MM/YYYY") : '-';

            // Calculate greeting based on current time
            const hour = moment().hour();
            let greeting = 'Selamat pagi';
            if (hour >= 11 && hour < 15) {
                greeting = 'Selamat siang';
            } else if (hour >= 15 && hour < 19) {
                greeting = 'Selamat sore';
            } else if (hour >= 19 || hour < 4) {
                greeting = 'Selamat malam';
            }

            const text = template
                .replace(/\${greeting}/g, greeting)
                .replace(/\${name}/g, clientName)
                .replace(/\${invoiceNumber}/g, invoiceNo)
                .replace(/\${dueDate}/g, dateStr);

            const appScheme = GlobalConfig.mobileAppScheme || 'jejak';
            const mobileDeeplink = `${appScheme}://invoice-detail/${invoice.id}`;
            const textWithLink = `${text}\n\natau dapat dilihat di: ${mobileDeeplink}`;

            setReminderText(textWithLink);
        }
    }, [invoice, activeSurface, randomTrigger]);

    useEffect(() => {
        init();
    }, []);

    const init = async () => {
        setLoaded(false);
        const _invoice = await IncomeInvoiceService.get(props.invoiceId);
        setInvoice(_invoice);
        setLoaded(true);
    }

    const getCommandBarItems = (_invoice: IIncomeInvoiceResourceProps) => {
        const items: ICommandBarItemProps[] = [];
        const farItems: ICommandBarItemProps[] = [];

        items.push({
            key: 'download',
            text: 'Download',
            onRender: () => {
                return <PrimaryButton text={"Download"} onRenderIcon={() => <FontAwesomeIcon icon={faDownload} />} onClick={() => {
                    _onDownloadInvoice('docx');
                }} styles={{ root: { marginRight: 20 } }} />
            }
        });

        if (PermissionsService.hasPermission(['incomes.invoices.update.all'], user.permissions) && _invoice.status !== 'Cancelled') {
            items.push({
                key: 'update',
                text: 'Update',
                onRender: () => {
                    return <ActionButton text={"Update"} onRenderIcon={() => <FontAwesomeIcon icon={faEdit} />} onClick={() => setActiveSurface('invoice.update')} />
                }
            });
        }

        if (Number(_invoice.unpaid) > 0 && PermissionsService.hasPermission(['incomes.invoices.create'], user.permissions)) {
            let remainingAmount = Number(_invoice.totalAmount);
            _invoice.parts.forEach((part) => {
                remainingAmount -= Number(part.amount)
            });

            if (remainingAmount > 0) {
                items.push({
                    key: 'part',
                    text: 'Create part',
                    onRender: () => {
                        return <ActionButton text={"Create part"} onRenderIcon={() => <FontAwesomeIcon icon={faReceipt} />} onClick={() => setActiveSurface('invoice.part.create')} />
                    }
                })
            }
        }

        const hasRecordPermission = PermissionsService.hasPermission(['incomes.invoices.payments.record'], user.permissions);
        if (Number(_invoice.unpaid) > 0 && hasRecordPermission) {
            items.push({
                key: 'recordPayment',
                text: 'Record Payment',
                onRender: () => {
                    return <ActionButton text={"Record Payment"} onRenderIcon={() => <FontAwesomeIcon icon={faPlus} />} onClick={() => setActiveSurface('payment.record')} />
                }
            });
        }

        items.push({
            key: 'versionHistory',
            text: 'Version History',
            onRender: () => {
                return <ActionButton text={"Version History"} onRenderIcon={() => <FontAwesomeIcon icon={faHistory} />} onClick={() => setActiveSurface('invoice.histories')} />
            }
        });

        items.push({
            key: 'reminderMessage',
            text: 'Reminder Message',
            onRender: () => {
                return <ActionButton text={"Reminder Message"} onRenderIcon={() => <FontAwesomeIcon icon={faComment} />} onClick={() => setActiveSurface('invoice.reminder')} />
            }
        });

        /*if (PermissionsService.hasPermission(['incomes.invoices.cancel'], user.permissions) && _invoice.status !== 'Cancelled') {
            farItems.push({
                key: 'cancel',
                text: 'Cancel',
                onRender: () => {
                    return <ActionButton text={"Cancel"} iconProps={{ iconName: "Cancel" }} onClick={() => setActiveSurface('invoice.cancel')} />
                }
            });
        }*/

        return { items, farItems };
    }

    const _onSurfaceDismissed = (refresh?: boolean) => {
        if (activeSurface === 'delete' && refresh) {
            props.onDismissed(true);
        }

        setActiveSurface(undefined);

        if (refresh) { init(); }
    }

    const _onDownloadInvoice = async (format: string) => {
        try {
            setDownloading(true);
            const name = IncomeInvoiceService.getInvoiceNumber(props.invoiceId);
            await IncomeInvoiceService.download(props.invoiceId, format, name);

            banner.add({
                key: 'download_invoice_success',
                text: `Invoice "${name}" downloaded successfully`,
                icon: faCheck,
                variant: 'success'
            });
            setDownloading(false);
        } catch (e) {
            setDownloading(false);
            banner.add({
                key: 'download_invoice_error',
                text: 'Failed to download invoice. Error: ' + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
        }
    }

    return <Panel headerText={invoice && (invoice?.name || "").trim() !== "" ? invoice.name : 'Invoice Details'}
        isOpen={true}
        type={PanelType.large}
        onDismiss={() => props.onDismissed(false)}>
        <Stack tokens={{ childrenGap: 20 }} styles={{ root: { marginTop: 20 } }}>
            {!loaded ? <Stack horizontalAlign={"baseline"}><Spinner size={SpinnerSize.medium} labelPosition={"right"} label={"Retrieving invoice details ..."} /></Stack> : null}
            {
                loaded && invoice ? <>
                    {invoice.status === 'Paid' ? <SimpleMessageBarComponent properties={{
                        type: MessageBarType.success,
                        text: `This invoice has been fully paid on ${moment(invoice.paidOn).format("DD/MM/YYY")}`
                    }} /> : null}
                    {invoice.status === 'Cancelled' ? <SimpleMessageBarComponent properties={{
                        type: MessageBarType.warning,
                        text: `This invoice has been cancelled${invoice.actionedBy ? " by " + invoice.actionedBy.name : ""}. Justification: ${invoice.justification}`
                    }} /> : null}
                    <CommandBar {...getCommandBarItems(invoice)} styles={{ root: { margin: 0, padding: 0 } }} />
                    <Stack horizontal tokens={{ childrenGap: 20 }}>
                        <Details title={"Invoice number"}
                            text={"#" + IncomeInvoiceService.getInvoiceNumber(invoice.id)}
                            style={{ width: '25%' }} />
                        {invoice.status === 'Paid' ? <Details title={`Paid on`}
                            text={moment(invoice.paidOn).format("DD/MM/YYYY")}
                            style={{ width: '25%' }} /> : null}
                        {invoice.status === 'Unpaid' ? <Details title={`Due date`}
                            text={GeneralService.getFriendlyDateFormat(invoice.dueDate)}
                            style={{ width: '25%' }} /> : null}
                        <Details title={`Subsidiary`}
                            text={invoice.order?.subsidiary.name || invoice.subsidiary?.name || "-"}
                            style={{ width: '25%' }} />
                        <Details title={`Transfer to`} style={{ width: '25%' }}>
                            {invoice.bankAccount ? <>
                                <Text>{invoice.bankAccount.accountName}</Text>
                                <Text size={'small'}>{invoice.bankAccount.accountNumber} - {invoice.bankAccount.bank.name}</Text>
                            </> : null}
                        </Details>
                    </Stack>
                    <Stack>
                        <Details title={'Notes'} text={invoice.notes || "-"}></Details>
                    </Stack>
                    <Stack className='divider'></Stack>
                    <Details title={`Billed to`}
                        text={invoice.order?.company?.name || invoice.company?.name || "-"}
                        style={{ width: '25%' }} />
                    <Stack className='divider'></Stack>
                    <Stack horizontal tokens={{ childrenGap: 20 }}>
                        <Details title={"Total amount (inc. PPn & PPh)"}
                            text={`Rp. ${GeneralService.getNumberWithSeparator(Number(invoice.totalAmount))}`}
                            style={{ width: '25%' }} />
                        <Details title={"PPn"}
                            text={`Rp. ${GeneralService.getNumberWithSeparator(Number(invoice.ppn))} (${invoice.ppnPercentage !== 'custom' ? GeneralService.getNumberWithSeparator(invoice.ppnPercentage) + "%" : "custom"})`}
                            style={{ width: '25%' }} />
                        <Details title={"PPh"}
                            text={`Rp. ${GeneralService.getNumberWithSeparator(Number(invoice.pph))} (${invoice.pphPercentage !== 'custom' ? GeneralService.getNumberWithSeparator(invoice.pphPercentage) + "%" : "custom"})`}
                            style={{ width: '25%' }} />
                        <Details title={"Invoice amount (Total amount - PPh)"}
                            text={`Rp. ${GeneralService.getNumberWithSeparator(Number(invoice.invoiceAmount))}`}
                            style={{ width: '25%' }} />
                    </Stack>
                    <Stack className='divider'></Stack>
                    <Stack horizontal tokens={{ childrenGap: 20 }}>
                        <Details title={"Paid"}
                            text={`Rp. ${GeneralService.getNumberWithSeparator(Number(invoice.paid))}`}
                            className={{ text: "color-green" }}
                            style={{ width: '25%' }} />
                        <Details title={"Pending"}
                            text={`Rp. ${GeneralService.getNumberWithSeparator(Number(invoice.pending))}`}
                            className={{ text: "color-yellow" }}
                            style={{ width: '25%' }} />
                        <Details title={"Unpaid"}
                            text={`Rp. ${GeneralService.getNumberWithSeparator(Number(invoice.unpaid))}`}
                            className={{ text: "color-red" }}
                            style={{ width: '25%' }} />
                        <Stack styles={{ root: { width: '25%' } }}> </Stack>
                    </Stack>
                    <Stack className='divider'></Stack>
                    <Pivot className={'linePivot'}>
                        {(invoice.items || []).length > 0 ? <PivotItem key={'itemsList'} headerText={"Items List"}>
                            <Stack styles={{ root: { padding: 10 } }}>
                                <IncomeInvoiceRelatedItems invoice={invoice} />
                            </Stack>
                        </PivotItem> : null}
                        {invoice.parts.length > 0 ? <PivotItem key={'partsList'} headerText={"Parts"}>
                            <IncomeInvoicePartsList invoiceId={invoice.id} parts={invoice.parts} hideCommandBar />
                        </PivotItem> : null}
                        {invoice.order ? <PivotItem key={'orderDetails'} headerText={"Order Details"}>
                            <OrdersList orders={[invoice.order]} hideStatusFilter hideCommandBar hideFilter hideCreateOrder hideSearch
                                columns={['orderNumber', 'customer', 'batch', 'deliveryDetails', 'deliveryDetails', 'price']} />
                        </PivotItem> : null}
                        {invoice.order ? <PivotItem key={'itemsList'} headerText={"Related Items"}>
                            <Stack styles={{ root: { padding: 10 } }}>
                                <IncomeInvoiceOrderItems invoice={invoice} />
                            </Stack>
                        </PivotItem> : null}
                        <PivotItem key={'paymentsList'} headerText={"Payments List"}>
                            <Stack styles={{ root: { padding: 10 } }}>
                                <IncomePaymentsList variant='plain' invoiceId={invoice.id} hideCommandBar={true} />
                            </Stack>
                        </PivotItem>
                        <PivotItem key={'documentList'} headerText={"Document List"}>
                            <Stack styles={{ root: { padding: 10 } }}>
                                <IncomeInvoiceDocumentsList variant='plain' orderInvoiceId={invoice.id} />
                            </Stack>
                        </PivotItem>
                    </Pivot>
                </> : null
            }
        </Stack>
        {downloading ? <LoadingDialogComponent title='Downloading Invoice' secondaryText="Please wait. Your invoice will be downloaded momentarily." /> : null}
        {activeSurface === 'payment.record' ? <IncomePaymentForm invoiceId={props.invoiceId} onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'invoice.update' && invoice ? <IncomeInvoiceForm invoiceId={invoice.id} onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'invoice.cancel' && invoice ? <IncomeInvoiceCancelForm invoice={invoice} onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'invoice.histories' && invoice ? <IncomeInvoiceVersionHistory invoiceId={invoice.id} onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'invoice.part.create' && invoice ? <IncomeInvoicePartForm invoiceId={invoice.id} onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'invoice.reminder' && invoice ? (
            <Panel
                headerText="Generate Invoice Reminder"
                isOpen={true}
                type={PanelType.medium}
                onDismiss={() => setActiveSurface(undefined)}
                isFooterAtBottom={true}
                onRenderFooterContent={() => {
                    const copyToClipboard = () => {
                        navigator.clipboard.writeText(reminderText);
                        banner.add({
                            key: 'copy_reminder_success',
                            text: 'Reminder text copied to clipboard!',
                            icon: faCheck,
                            variant: 'success'
                        });
                    };

                    const sendWhatsApp = () => {
                        const rawPhone = invoice.order?.customer?.phoneNumber || invoice.order?.company?.phoneNumber || invoice.company?.phoneNumber || '';
                        let cleanPhone = rawPhone.replace(/\D/g, '');
                        if (cleanPhone.startsWith('0')) {
                            cleanPhone = '62' + cleanPhone.substring(1);
                        }
                        const url = `https://api.whatsapp.com/send?phone=${cleanPhone}&text=${encodeURIComponent(reminderText)}`;
                        window.open(url, '_blank');
                    };

                    return (
                        <Stack horizontal tokens={{ childrenGap: 10 }}>
                            <PrimaryButton text="Copy Text" onRenderIcon={() => <FontAwesomeIcon icon={faCopy} />} onClick={copyToClipboard} />
                            <DefaultButton text="Send WhatsApp" onRenderIcon={() => <FontAwesomeIcon icon={faPaperPlane} />} onClick={sendWhatsApp} />
                            <DefaultButton text="Close" onClick={() => setActiveSurface(undefined)} />
                        </Stack>
                    );
                }}
            >
                <Stack tokens={{ childrenGap: 20 }} styles={{ root: { marginTop: 20 } }}>
                    <Stack>
                        <Text size="medium" style={{ fontWeight: 'bold' }}>{detectedCategory}</Text>
                    </Stack>
                    <TextField
                        label="Pesan Pengingat (Variasi Acak)"
                        multiline
                        rows={8}
                        autoAdjustHeight
                        readOnly
                        value={reminderText}
                    />
                    <Stack horizontalAlign="start">
                        <ActionButton
                            onRenderIcon={() => <FontAwesomeIcon icon={faRotate} />}
                            text="Ganti Variasi Pesan (Acak Ulang)"
                            onClick={() => setRandomTrigger(prev => prev + 1)}
                        />
                    </Stack>
                </Stack>
            </Panel>
        ) : null}
        {/*activeSurface === 'delete' && invoice ? <DeleteOrderInvoiceForm invoice={invoice} onDismissed={_onSurfaceDismissed} /> : null*/}
    </Panel>
});

export default IncomeInvoiceDetails;
